import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
  CircularProgress,
} from "@mui/material";
import { notification } from "antd";
import { useContactUsMutation } from "../../../Redux/apis/ApiSlice";
import { Email, Phone, AccessTime } from "@mui/icons-material";
import Navbar from "../Home/NavBar/Navbar";
import AffordablePricing from "../Home/AffordablePricing/AffordablePricing";
import Footer from "../Home/Footer/Footer";

const ContactUs = () => {
  const [sendContactEmail] = useContactUsMutation();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    message: "",
  });

  useEffect(() => {
    // Scroll to top when the page loads
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.email ||
      !formData.phone ||
      !formData.message
    ) {
      notification.error({ message: "Please fill in all fields!" });
      return;
    }
    setLoading(true);
    try {
      await sendContactEmail(formData);
      notification.success({ message: "Message sent successfully!" });
      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        message: "",
      });
     
    } catch (error) {
      notification.error({
        message: error.data?.message || "Failed to send message!",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          paddingLeft: "25px",
          paddingRight: "25px",
          boxShadow: "0 1px 5px rgba(0,0,0,0.1)",
        }}
      >
        <Navbar />
      </Box>

      <Container maxWidth="lg" sx={{ mt: 6 }}>
        <Grid container spacing={6} justifyContent="center">
          {/* Left Side - Form */}
          <Grid item xs={12} md={6}>
            <Typography variant="h4" sx={styles.title}>
              Get In Touch
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mt: 2, textAlign: {xs: "left", md: "justify"} }}>
              We’d love to hear from you! Fill out the form below.
            </Typography>

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    multiline
                    rows={4}
                    required
                  />
                </Grid>
              </Grid>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{ mt: 2, backgroundColor: "#535353", borderRadius: "4px" }}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} sx={{ color: "white" }} />
                ) : (
                  "SEND"
                )}
              </Button>
            </Box>
          </Grid>

          {/* Right Side - Contact Info */}
          <Grid item xs={12} md={5}>
            <Box>
              <Typography variant="h4" sx={styles.title}>
                Contact Us
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 2, textAlign: {xs: "left", md: "justify"} }}>
                We are always happy to assist you. Reach out to us anytime!
              </Typography>

              <Stack
                spacing={3}
                alignItems="center"
                sx={{
                  mx: "auto",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  alignItems: "flex-start",
                  mt: 4,
                }}
              >
                {/* Contact Cards */}
                <ContactCard
                  icon={<Phone sx={{ fontSize: 30, color: "#535353" }} />}
                  title="Contact Number"
                  text="(404) 808-4417"
                />
                <ContactCard
                  icon={<Email sx={{ fontSize: 30, color: "#535353" }} />}
                  title="Email Address"
                  text="help@gxtickets.com"
                  right={true}
                />
                <ContactCard
                  icon={<AccessTime sx={{ fontSize: 30, color: "#535353" }} />}
                  title="Hours"
                  text="Mon - Fri 6 am to 8 pm EST"
                />
              </Stack>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <Box sx={{ mt: { xs: 10, md: 20 } }}>
        <AffordablePricing />
      </Box>

      <Box sx={{ mt: { xs: 10, md: 20 } }}>
        <Footer />
      </Box>
    </>
  );
};

const ContactCard = ({ icon, title, text, right }) => (
  <Paper
    elevation={0}
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
      p: 2,
      borderRadius: 3,
      minWidth: 280,
      width: { xs: "100%", sm: "auto" },
      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
      ...(right ? { right: { xs: 0, sm: "-30%" } } : {}), // Apply `right` only if `right` is true
      position: "relative",
    }}
  >
    {icon}
    <Box>
      <Typography variant="caption" fontWeight="bold" color="text.secondary">
        {title}
      </Typography>
      <Typography variant="body1" fontWeight="500">
        {text}
      </Typography>
    </Box>
  </Paper>
);

export default ContactUs;

const styles = {
  title: {
    fontWeight: 700,
    fontSize: { xs: 24, sm: 32, md: 40 },
  },
};
